// Formulario de solicitud de prueba gratuita. Lo usan la página de referido
// (pages/ReferralLanding.js) y la landing (pages/LandingPage.js, aparcada).
import React, { useState } from 'react';
import axios from 'axios';
import { Check, Gift, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { AnimatedSection } from './utils';
import { WHATSAPP_MANKLAR_URL, WEB_MANKLAR_URL } from '../../lib/contacto';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// LEAD FORM SECTION
// ============================================================
export function LeadFormSection({ formRef, referralCode, referrerName }) {
  // referral_code sólo guarda lo que se escribe a mano en el campo (que se
  // muestra cuando no llega referralCode); el código de la URL se lee del
  // prop al enviar, porque puede llegar después del primer render.
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    referral_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.address
      };
      
      const codigo = (referralCode || formData.referral_code).trim();
      if (codigo) {
        payload.referral_code = codigo;
      }
      
      await axios.post(`${API_URL}/api/leads`, payload);
      setSubmitted(true);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al enviar la solicitud';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Success state - Premium confirmation page
  if (submitted) {
    return (
      <section ref={formRef} id="form" className="py-16 sm:py-24 md:py-32 relative">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-emerald-500/20 to-sky-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6 sm:mb-8">
              <Check className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-400" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white font-['Outfit'] mb-4">
              Solicitud recibida correctamente
            </h2>
            
            <p className="text-base sm:text-lg text-zinc-400 font-['Manrope'] mb-4 max-w-md mx-auto">
              Hemos recibido tu solicitud. Un asesor de Manklar contactará contigo 
              para coordinar tu prueba gratuita.
            </p>
            
            {referrerName && (
              <p className="text-sm text-emerald-400 font-['Manrope'] mb-8">
                Tu recomendación de <span className="font-semibold">{referrerName}</span> ha quedado registrada correctamente.
              </p>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mt-8">
              <a
                href={WHATSAPP_MANKLAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="h-12 sm:h-14 px-6 sm:px-8 bg-[#25D366] text-black font-semibold rounded-full hover:bg-[#20bd5a] transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <MessageCircle className="w-4 sm:w-5 h-4 sm:h-5" />
                Contactar por WhatsApp
              </a>
              <a
                href={WEB_MANKLAR_URL}
                className="h-12 sm:h-14 px-6 sm:px-8 bg-white/5 border border-white/10 text-white font-medium rounded-full hover:bg-white/10 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                Volver a la web
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    );
  }

  return (
    <section ref={formRef} id="form" className="py-16 sm:py-24 md:py-32 relative">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="text-center mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white font-['Outfit'] tracking-tight mb-4">
            Solicita tu <span className="font-medium">prueba gratuita</span>
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 font-['Manrope']">
            Sin compromiso. Te contactaremos para agendar la instalación.
          </p>
        </AnimatedSection>
        
        {referrerName && (
          <AnimatedSection delay={100} className="mb-6">
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3">
              <Gift className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-300 font-['Manrope']">
                Has sido invitado por <span className="font-semibold">{referrerName}</span>
              </p>
            </div>
          </AnimatedSection>
        )}
        
        <AnimatedSection delay={200}>
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-xs sm:text-sm text-zinc-500 uppercase tracking-wider font-['Manrope'] mb-2">
                Nombre completo *
              </label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Tu nombre"
                className="h-12 sm:h-14 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus:border-white/30 font-['Manrope'] text-base"
                data-testid="lead-name-input"
                required
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-xs sm:text-sm text-zinc-500 uppercase tracking-wider font-['Manrope'] mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="tu@email.com"
                  className="h-12 sm:h-14 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus:border-white/30 font-['Manrope'] text-base"
                  data-testid="lead-email-input"
                  required
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm text-zinc-500 uppercase tracking-wider font-['Manrope'] mb-2">
                  Teléfono *
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+34 600 000 000"
                  className="h-12 sm:h-14 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus:border-white/30 font-['Manrope'] text-base"
                  data-testid="lead-phone-input"
                  required
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs sm:text-sm text-zinc-500 uppercase tracking-wider font-['Manrope'] mb-2">
                Dirección (opcional)
              </label>
              <Input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Calle, número, ciudad..."
                className="h-12 sm:h-14 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus:border-white/30 font-['Manrope'] text-base"
                data-testid="lead-address-input"
              />
            </div>
            
            {!referralCode && (
              <div>
                <label className="block text-xs sm:text-sm text-zinc-500 uppercase tracking-wider font-['Manrope'] mb-2">
                  Código de referido (opcional)
                </label>
                <Input
                  type="text"
                  value={formData.referral_code}
                  onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.toUpperCase() })}
                  placeholder="Ej: JUAN123"
                  className="h-12 sm:h-14 bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl focus:border-white/30 font-['Manrope'] uppercase text-base"
                  data-testid="lead-referral-input"
                />
              </div>
            )}
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 sm:h-14 bg-white text-black font-semibold rounded-full hover:bg-zinc-200 hover:scale-[1.02] transition-all duration-300 text-base sm:text-lg shadow-xl shadow-white/10"
              data-testid="lead-submit-btn"
            >
              {loading ? 'Enviando...' : 'Solicitar prueba gratuita'}
            </Button>
            
            <p className="text-[10px] sm:text-xs text-zinc-600 text-center font-['Manrope']">
              Al enviar aceptas nuestra política de privacidad. No compartiremos tus datos.
            </p>
          </form>
        </AnimatedSection>
      </div>
    </section>
  );
}
